import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosConfig';
import { Plus, Trash2, Edit, Search, ChevronRight, Download } from 'lucide-react';
import Header from '../../../components/Header/Header';
import Footer from '../../../components/Footer/Footer';
import { Button, Table, message, Input, Modal, Dropdown } from 'antd';
import { PlusOutlined, DeleteOutlined, MinusOutlined, DownloadOutlined } from '@ant-design/icons';
import { ExportPopup } from './ExportPopup';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const BatchIndent = () => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [newBatchDescription, setNewBatchDescription] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showExportPopup, setShowExportPopup] = useState(false);

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/batch-indents');
      setBatches(response.data);
    } catch (error) {
      console.error('Error fetching batches:', error);
      message.error('Failed to fetch batches');
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBatchData = async (batchId) => {
    try {
      const response = await axiosInstance.get(`/batch-indents/${batchId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching batch data:', error);
      throw error;
    }
  };

  const handleCreateBatch = async () => {
    try {
      await axiosInstance.post('/batch-indents', {
        description: newBatchDescription
      });
      message.success('Batch created successfully');
      setIsCreateModalOpen(false);
      setNewBatchDescription('');
      fetchBatches();
    } catch (error) {
      console.error('Error creating batch:', error);
      message.error('Failed to create batch');
    }
  };

  const handleDeleteBatch = async (batchId) => {
    Modal.confirm({
      title: 'Delete Batch',
      content: 'Are you sure you want to delete this batch? This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await axiosInstance.delete(`/batch-indents/${batchId}`);
          message.success('Batch deleted successfully');
          fetchBatches();
        } catch (error) {
          console.error('Error deleting batch:', error);
          message.error('Failed to delete batch');
        }
      }
    });
  };

  const handleRemoveIndent = async (batchId, indentId) => {
    try {
      await axiosInstance.delete(`/batch-indents/${batchId}/indents/${indentId}`);
      message.success('Indent removed from batch successfully');
      fetchBatches();
    } catch (error) {
      console.error('Error removing indent:', error);
      message.error('Failed to remove indent from batch');
    }
  };

  const handleAddIndent = (batchId) => {
    setSelectedBatchId(batchId);
    setIsAddModalOpen(true);
  };

  const handleIndentAdded = () => {
    setIsAddModalOpen(false);
    fetchBatches();
  };

  const handleExport = async (batch) => {
    try {
      const batchData = await fetchBatchData(batch.BatchID);
      setSelectedBatch(batchData);
      setShowExportPopup(true);
    } catch (error) {
      console.error('Error preparing export:', error);
      message.error('Failed to prepare export');
    }
  };

  const exportMenuItems = (batch) => [
    {
      key: 'export',
      label: 'Export',
      onClick: () => handleExport(batch)
    }
  ];

  const filteredBatches = batches.filter(batch => 
    batch.BatchNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
    batch.Description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <div className="flex-grow p-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Batch Indents</h1>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsCreateModalOpen(true)}
            >
              Create New Batch
            </Button>
          </div>

          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <input
                type="text"
                placeholder="Search batches..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="absolute right-3 top-2.5 text-gray-400" size={20} />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredBatches.map((batch) => (
                <div
                  key={batch.BatchID}
                  className="bg-white rounded-lg shadow-md p-4 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => navigate(`/batch-indent/${batch.BatchID}`)}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">Batch #{batch.BatchNumber}</h3>
                      <p className="text-sm text-gray-600 mt-1">{batch.Description}</p>
                    </div>
                    <div className="flex space-x-2">
                      <Dropdown
                        menu={{ items: exportMenuItems(batch) }}
                        placement="bottomRight"
                        trigger={['click']}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button
                          icon={<DownloadOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExport(batch);
                          }}
                        >
                          Export
                        </Button>
                      </Dropdown>
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteBatch(batch.BatchID);
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                  <div className="border-t pt-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Total Indents: {batch.IndentCount || 0}
                    </h4>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Modal
        title="Create New Batch"
        open={isCreateModalOpen}
        onCancel={() => {
          setIsCreateModalOpen(false);
          setNewBatchDescription('');
        }}
        onOk={handleCreateBatch}
        okText="Create"
        cancelText="Cancel"
      >
        <Input
          placeholder="Enter batch description"
          value={newBatchDescription}
          onChange={(e) => setNewBatchDescription(e.target.value)}
        />
      </Modal>

      {showExportPopup && selectedBatch && (
        <ExportPopup
          onClose={() => {
            setShowExportPopup(false);
            setSelectedBatch(null);
          }}
          batch={selectedBatch}
          customFilename={`batch-indent-${selectedBatch.BatchNumber}`}
        />
      )}

      <Footer />
    </div>
  );
};

export default BatchIndent; 